import React from 'react';
import styled, { keyframes } from 'styled-components';
import vars from './vars';
import { Button } from './Button';

const TOAST_MARGIN = 24;
export const TOAST_DURATION_MS = 5000;
export const PERSISTENT = process.env.REACT_APP_HARNESS === 'true';

const shrink = keyframes`
    from { width: 100%; }
    to { width: 0%; }
`;

type ToastType = 'success' | 'error' | 'warning';

const getBackgroundColor = (type: ToastType) => {
    switch (type) {
        case 'success':
            return vars.successTintSoft;
        case 'warning':
            return vars.warningTintSoft;
        case 'error':
            return vars.dangerTintSoft;
    }
};

const Container = styled.div<{ type: ToastType; right: number }>`
    position: fixed;
    right: ${(p) => p.right}px;
    background-color: ${(p) => getBackgroundColor(p.type)};
    color: ${vars.foregroundColor};
    border: 1px solid ${vars.dividerColor};
    padding: 16px 20px;
    padding-bottom: 12px;
    border-radius: 8px;
    font-size: 1rem;
    max-width: min(400px, calc(100vw - ${TOAST_MARGIN * 2}px));
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    overflow: hidden;
    z-index: 1000;
`;

const ProgressBarContainer = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background-color: rgba(0, 0, 0, 0.08);
`;

const getProgressColor = (type: ToastType) => {
    switch (type) {
        case 'success':
            return vars.accentColor;
        case 'warning':
            return vars.warningColor;
        case 'error':
            return vars.dangerColorDark;
    }
};

const ProgressBar = styled.div<{ type: ToastType; duration: number }>`
    height: 100%;
    background-color: ${(p) => getProgressColor(p.type)};
    animation: ${shrink} ${(p) => p.duration}ms linear forwards;
`;

const Message = styled.span`
    width: 100%;
    font-weight: 500;
    line-height: 1.5;
`;

const CloseButton = styled(Button)`
    align-self: flex-end;
    background-color: transparent;
    padding: 6px 12px;
    font-size: 0.9rem;
    transition: background-color 0.2s;

    &:hover {
        background-color: rgba(0, 0, 0, 0.05);
    }
`;

interface ToastProps {
    message: string | null;
    type?: ToastType;
    onClose: () => void;
    toastKey?: number;
    duration?: number;
    footerId?: string;
    containerId?: string;
    scrollContainerId?: string;
}

export function Toast({
    message,
    type = 'error',
    onClose,
    toastKey,
    duration = TOAST_DURATION_MS,
    footerId,
    containerId,
    scrollContainerId,
}: ToastProps) {
    const [bottomOffset, setBottomOffset] = React.useState(TOAST_MARGIN);
    const [rightOffset, setRightOffset] = React.useState(TOAST_MARGIN);

    React.useLayoutEffect(() => {
        let rafId: number | null = null;
        let isFooterIntersecting = false;
        let observedFooter: Element | null = null;

        const updatePosition = () => {
            const windowHeight = window.innerHeight;
            const windowWidth = window.innerWidth;
            let targetBottom = TOAST_MARGIN;
            let targetRight = TOAST_MARGIN;

            if (containerId) {
                const container = document.getElementById(containerId);
                if (container) {
                    const containerRect = container.getBoundingClientRect();
                    const containerRightOffset = windowWidth - containerRect.right;
                    targetRight = Math.max(TOAST_MARGIN, containerRightOffset + TOAST_MARGIN);
                }
            }

            if (footerId) {
                const footer = document.getElementById(footerId);
                if (footer) {
                    const footerRect = footer.getBoundingClientRect();
                    const footerVisibleHeight = Math.max(0, windowHeight - footerRect.top);
                    if (footerVisibleHeight > 0) {
                        targetBottom = TOAST_MARGIN + footerVisibleHeight;
                    }
                }
            }

            setBottomOffset(targetBottom);
            setRightOffset(targetRight);
        };

        const scheduleUpdate = (isScrollEvent = false) => {
            if (isScrollEvent && !isFooterIntersecting) return;
            if (rafId !== null) return;
            rafId = requestAnimationFrame(() => {
                rafId = null;
                updatePosition();
            });
        };

        const onScroll = () => scheduleUpdate(true);
        const onResize = () => scheduleUpdate(false);

        const io = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                isFooterIntersecting = entry.isIntersecting;
                scheduleUpdate(false);
            },
            { threshold: 0 },
        );

        const refreshObserver = () => {
            if (!footerId) return;
            const footer = document.getElementById(footerId);
            if (footer !== observedFooter) {
                if (observedFooter) io.unobserve(observedFooter);
                if (footer) {
                    io.observe(footer);
                    scheduleUpdate(false);
                }
                observedFooter = footer;
            }
        };

        // Scroll listener target
        let scrollTarget: Element | null = null;
        if (scrollContainerId) {
            scrollTarget = document.getElementById(scrollContainerId);
            if (scrollTarget) {
                scrollTarget.addEventListener('scroll', onScroll, { passive: true });
            }
        } else {
            document.addEventListener('scroll', onScroll, { passive: true, capture: true });
        }

        window.addEventListener('resize', onResize, { passive: true });

        // MutationObserver to detect when footer/container/scroll elements appear dynamically
        const mutationObserver = new MutationObserver(() => {
            refreshObserver();

            // Re-attach scroll listener if scrollContainerId target appeared
            if (scrollContainerId && !scrollTarget) {
                const element = document.getElementById(scrollContainerId);
                if (element) {
                    scrollTarget = element;
                    scrollTarget.addEventListener('scroll', onScroll, { passive: true });
                }
            }

            scheduleUpdate(false);
        });
        mutationObserver.observe(document.body, { childList: true, subtree: true });

        refreshObserver();
        updatePosition();

        return () => {
            if (rafId !== null) cancelAnimationFrame(rafId);

            if (scrollContainerId) {
                if (scrollTarget) scrollTarget.removeEventListener('scroll', onScroll);
            } else {
                document.removeEventListener('scroll', onScroll, { capture: true });
            }

            window.removeEventListener('resize', onResize);
            mutationObserver.disconnect();
            io.disconnect();
        };
    }, [footerId, containerId, scrollContainerId]);

    React.useEffect(() => {
        if (!message || PERSISTENT) return;

        const timer = setTimeout(() => {
            onClose();
        }, duration);

        return () => clearTimeout(timer);
    }, [message, onClose, duration]);

    if (!message) return null;

    return (
        <Container
            type={type}
            right={rightOffset}
            style={{ bottom: `${bottomOffset}px` }}
            data-testid={`toast-${type}`}
        >
            <Message>{message}</Message>
            <CloseButton onClick={onClose} aria-label="Fechar notificação">
                Fechar
            </CloseButton>
            {!PERSISTENT && (
                <ProgressBarContainer>
                    <ProgressBar key={toastKey ?? message} type={type} duration={duration} />
                </ProgressBarContainer>
            )}
        </Container>
    );
}
